import React, { useState, useEffect } from "react";
import {
  TrendingDown,
  TrendingUp,
  Users,
  AlertCircle,
  Download,
  RefreshCw,
  BarChart3,
  Clock,
  Briefcase,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { useCustomAlert } from "../../../hooks/useCustomAlert";
import hrAnalyticsService from "../../../api/services/hrAnalyticsService";
import apiClient from "../../../api/apiClient";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import Button from "../../../components/common/Button/Button";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { ChevronDown } from "lucide-react";

// CSV Export Function
const exportToCSV = (attritionData) => {
  const rows = [];

  // Add header row
  rows.push([
    'Attrition Analysis Report',
    `Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
  ]);
  rows.push([]); // Empty row

  // Key Metrics Section
  rows.push(['Key Metrics']);
  rows.push(['Metric', 'Value']);
  rows.push(['Total Exits', attritionData?.totalExits || 0]);
  rows.push(['Voluntary Exits', attritionData?.voluntaryExits || 0]);
  rows.push(['Involuntary Exits', attritionData?.involuntaryExits || 0]);
  rows.push([]); // Empty row

  // Monthly Attrition Trends Section
  if (attritionData?.monthlyTrends && attritionData.monthlyTrends.length > 0) {
    rows.push(['Monthly Attrition Trends']);
    rows.push(['Month', 'Voluntary', 'Involuntary', 'Total']);

    attritionData.monthlyTrends.forEach((item) => {
      rows.push([
        item.month || '',
        item.voluntary || 0,
        item.involuntary || 0,
        item.total || 0
      ]);
    });
    rows.push([]); // Empty row
  }

  // Department-wise Attrition Section
  if (attritionData?.departmentAttrition && attritionData.departmentAttrition.length > 0) {
    rows.push(['Department-wise Attrition']);
    rows.push([
      'Department',
      'Total Staff',
      'Exits',
      'Attrition Rate (%)'
    ]);

    attritionData.departmentAttrition.forEach((dept) => {
      rows.push([
        dept.department || '',
        dept.totalEmployees || 0,
        dept.exits || dept.totalExits || 0,
        `${dept.rate || dept.attritionRate || 0}%`
      ]);
    });
    rows.push([]); // Empty row
  }

  // Recent Exits Summary Section
  if (attritionData?.recentExits && attritionData.recentExits.length > 0) {
    rows.push(['Recent Exits Summary']);
    rows.push([
      'Name',
      'Email',
      'Department',
      'Exit Date',
      'Type'
    ]);

    attritionData.recentExits.forEach((exit) => {
      const exitDate = exit.lastWorkingDate
        ? new Date(exit.lastWorkingDate).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
        : 'N/A';

      const exitType = exit.resignationType === 'involuntary'
        ? 'Involuntary'
        : exit.resignationType === 'retirement'
          ? 'Retirement'
          : exit.resignationType === 'contract_end'
            ? 'Contract End'
            : 'Voluntary';

      rows.push([
        exit.employeeName || 'N/A',
        exit.employeeEmail || 'N/A',
        exit.department || 'N/A',
        exitDate,
        exitType
      ]);
    });
  }

  // Convert rows to CSV format
  const csvContent = rows.map(row => {
    return row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      const cellValue = String(cell || '');
      if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
        return `"${cellValue.replace(/"/g, '""')}"`;
      }
      return cellValue;
    }).join(',');
  }).join('\n');

  // Create and download CSV file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `Attrition_Analysis_Report_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const AttritionAnalysis = () => {
  const { user } = useAuth();
  const { showError, showSuccess } = useCustomAlert();

  const [selectedPeriod, setSelectedPeriod] = useState("current-month");
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [viewType, setViewType] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [attritionData, setAttritionData] = useState(null);

  const KpiCard = ({ icon: Icon, title, value, subtitle, color, trend }) => {
      let bgColor = "bg-[#0F2942]";
      if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
      else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
      else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
      else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
      else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

      return (
          <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
              <div className="space-y-4 flex-1">
                  <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                      {title}
                  </h3>
                  <div className="flex items-baseline space-x-2">
                      <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                          {value}
                      </span>
                      {(subtitle || trend) && (
                          <div className="flex items-center space-x-2">
                              {subtitle && (
                                  <span className="text-sm font-medium text-slate-500">
                                      {subtitle}
                                  </span>
                              )}
                              {trend && (
                                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${trend.color} shadow-sm`}>
                                      {trend.label}
                                  </span>
                              )}
                          </div>
                      )}
                  </div>
              </div>
              <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                  <Icon className="w-5 h-5" />
              </div>
          </div>
      );
  };

  // Load attrition data from API - using new department attrition API
  const loadAttritionData = async () => {
    try {
      setLoading(true);

      // Map selectedPeriod to months for trends
      let months = 6;
      if (selectedPeriod === "last-month") months = 1;
      else if (selectedPeriod === "quarter") months = 3;
      else if (selectedPeriod === "year") months = 12;
      else if (selectedPeriod === "all-data") months = 12; // Cap at 12 for trends

      // Fetch monthly trends and department attrition data
      const [trendsResponse, departmentResponse] = await Promise.all([
        hrAnalyticsService.getAttritionTrends(
          months,
          selectedPeriod,
          dateRange.startDate,
          dateRange.endDate
        ),
        hrAnalyticsService.getDepartmentAttrition({
          period: selectedPeriod,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }),
      ]);

      if (trendsResponse.trends && departmentResponse.departmentAttrition) {
        // Process the data to match our component structure
        const processedData = processAttritionDataFromAPI(
          trendsResponse.trends,
          departmentResponse
        );
        setAttritionData(processedData);
      } else {
        throw new Error("No trends or department data received");
      }

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttritionData();
  }, [selectedPeriod, dateRange]);

  // Process data from new department attrition API
  const processAttritionDataFromAPI = (trends, departmentResponse) => {

    // Calculate totals from trends
    const totalExits = trends.reduce((sum, trend) => sum + trend.total, 0);
    const voluntaryExits = trends.reduce(
      (sum, trend) => sum + trend.voluntary,
      0
    );
    const involuntaryExits = trends.reduce(
      (sum, trend) => sum + trend.involuntary,
      0
    );

    // Use department data from the new API
    const departmentAttrition = departmentResponse.departmentAttrition || [];
    const summary = departmentResponse.summary || {};
    const recentExits = departmentResponse.recentExits || [];

    return {
      totalExits: summary.totalExits || totalExits,
      voluntaryExits: summary.voluntaryExits || voluntaryExits,
      involuntaryExits: summary.involuntaryExits || involuntaryExits,
      monthlyTrends: trends,
      departmentAttrition,
      recentExits, // Recent exits with employee details
      clearances: [], // No individual clearance data from this API
    };
  };

  // Process trends data from HR Analytics API
  const processAttritionDataFromTrends = (trends, departmentData = null) => {
    // Calculate totals from trends
    const totalExits = trends.reduce((sum, trend) => sum + trend.total, 0);
    const voluntaryExits = trends.reduce(
      (sum, trend) => sum + trend.voluntary,
      0
    );
    const involuntaryExits = trends.reduce(
      (sum, trend) => sum + trend.involuntary,
      0
    );

    // Department analysis from department data
    let departmentAttrition = [];

    if (departmentData && departmentData.length > 0) {
      // Group clearances by department
      const departmentStats = {};

      departmentData.forEach((clearance) => {
        const dept = clearance.department;

        if (!departmentStats[dept]) {
          departmentStats[dept] = {
            totalEmployees: 0,
            exits: 0,
            voluntary: 0,
            involuntary: 0,
          };
        }

        // Count exits
        departmentStats[dept].exits++;
        if (clearance.clearanceType === "resignation") {
          departmentStats[dept].voluntary++;
        } else {
          departmentStats[dept].involuntary++;
        }
      });

      // Convert to array with realistic employee counts and percentages
      departmentAttrition = Object.entries(departmentStats).map(
        ([dept, stats]) => {
          // Estimate total employees based on department type and exits
          let estimatedTotalEmployees;
          switch (dept.toLowerCase()) {
            case "engineering":
              estimatedTotalEmployees = Math.max(stats.exits * 12, 450);
              break;
            case "sales":
              estimatedTotalEmployees = Math.max(stats.exits * 10, 280);
              break;
            case "marketing":
              estimatedTotalEmployees = Math.max(stats.exits * 15, 120);
              break;
            case "human resources":
            case "hr":
              estimatedTotalEmployees = Math.max(stats.exits * 22, 45);
              break;
            case "general":
              estimatedTotalEmployees = Math.max(stats.exits * 5, 50);
              break;
            case "finance":
              estimatedTotalEmployees = Math.max(stats.exits * 17, 85);
              break;
            case "customer support":
              estimatedTotalEmployees = Math.max(stats.exits * 10, 180);
              break;
            default:
              estimatedTotalEmployees = Math.max(stats.exits * 10, 100);
          }

          const attritionRate = (
            (stats.exits / estimatedTotalEmployees) *
            100
          ).toFixed(1);

          return {
            department: dept,
            totalEmployees: estimatedTotalEmployees,
            exits: stats.exits,
            voluntary: stats.voluntary,
            involuntary: stats.involuntary,
            rate: parseFloat(attritionRate),
            trend: "stable",
          };
        }
      );

    } else {
      // Fallback: Create a simple department breakdown
      if (totalExits > 0) {
        departmentAttrition.push({
          department: "All Departments",
          totalEmployees: 0,
          exits: totalExits,
          voluntary: voluntaryExits,
          involuntary: involuntaryExits,
          rate: 0,
          trend: "stable",
        });
      }
    }

    return {
      totalExits,
      voluntaryExits,
      involuntaryExits,
      monthlyTrends: trends,
      departmentAttrition,
      clearances: departmentData || [], // Use department data for individual clearances
    };
  };

  // Process raw clearance data into attrition insights with department-wise analysis
  const processAttritionData = (clearances) => {
    // Filter clearances - include all clearances for comprehensive analysis
    const recentExits = clearances.filter((clearance) => {
      return clearance.lastWorkingDate;
    });

    // Calculate overall metrics
    const totalExits = recentExits.length;
    const voluntaryExits = recentExits.filter(
      (c) => c.clearanceType === "resignation"
    ).length;
    const involuntaryExits = recentExits.filter(
      (c) => c.clearanceType === "termination"
    ).length;

    // Calculate monthly trends - show last 6 months
    const monthlyTrends = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

      // Filter exits for this specific month
      const monthExits = recentExits.filter((clearance) => {
        const exitDate = new Date(clearance.lastWorkingDate);
        return exitDate >= targetDate && exitDate < nextMonth;
      });

      const voluntary = monthExits.filter(
        (c) => c.clearanceType === "resignation"
      ).length;
      const involuntary = monthExits.filter(
        (c) => c.clearanceType === "termination"
      ).length;

      monthlyTrends.push({
        month: targetDate.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
        total: monthExits.length,
        voluntary,
        involuntary,
        rate: 0,
      });
    }

    // Department analysis with realistic employee counts and percentages
    const departmentStats = {};
    recentExits.forEach((clearance) => {
      const dept = clearance.department;
      if (!departmentStats[dept]) {
        departmentStats[dept] = { exits: 0, voluntary: 0, involuntary: 0 };
      }
      departmentStats[dept].exits++;
      if (clearance.clearanceType === "resignation") {
        departmentStats[dept].voluntary++;
      } else {
        departmentStats[dept].involuntary++;
      }
    });

    // Convert to array with realistic employee counts and percentages
    const departmentAttrition = Object.entries(departmentStats).map(
      ([dept, stats]) => {
        // Estimate total employees based on department type and exits
        let estimatedTotalEmployees;
        switch (dept.toLowerCase()) {
          case "engineering":
            estimatedTotalEmployees = Math.max(stats.exits * 12, 450);
            break;
          case "sales":
            estimatedTotalEmployees = Math.max(stats.exits * 10, 280);
            break;
          case "marketing":
            estimatedTotalEmployees = Math.max(stats.exits * 15, 120);
            break;
          case "human resources":
          case "hr":
            estimatedTotalEmployees = Math.max(stats.exits * 22, 45);
            break;
          case "general":
            estimatedTotalEmployees = Math.max(stats.exits * 5, 50);
            break;
          case "finance":
            estimatedTotalEmployees = Math.max(stats.exits * 17, 85);
            break;
          case "customer support":
            estimatedTotalEmployees = Math.max(stats.exits * 10, 180);
            break;
          default:
            estimatedTotalEmployees = Math.max(stats.exits * 10, 100);
        }

        const attritionRate = (
          (stats.exits / estimatedTotalEmployees) *
          100
        ).toFixed(1);

        return {
          department: dept,
          totalEmployees: estimatedTotalEmployees,
          exits: stats.exits,
          voluntary: stats.voluntary,
          involuntary: stats.involuntary,
          rate: parseFloat(attritionRate),
          trend: "stable",
        };
      }
    );


    return {
      totalExits,
      voluntaryExits,
      involuntaryExits,
      monthlyTrends,
      departmentAttrition,
      clearances: recentExits,
    };
  };

  // Load data on component mount

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up":
        return <TrendingUp className="h-4 w-4 text-red-500" />;
      case "down":
        return <TrendingDown className="h-4 w-4 text-green-500" />;
      default:
        return <div className="h-4 w-4 bg-gray-400 rounded-full" />;
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case "high":
        return "text-red-600 bg-red-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "low":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  // Show loading state - only for initial load to avoid flickering on updates
  if (loading && !attritionData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-[#0F2942] mx-auto mb-4" />
          <p className="text-gray-600">Loading attrition analysis...</p>
        </div>
      </div>
    );
  }

  // Show no data state
  if (!attritionData) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No Attrition Data
        </h3>
        <p className="text-gray-600 mb-4">
          No attrition data available for the selected period.
        </p>
        <button
          onClick={loadAttritionData}
          className="px-4 py-2 bg-[#0F2942] text-white rounded-md hover:bg-[#0B2033]"
        >
          Refresh Data
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            Attrition Analysis
          </h2>
          <p className="text-sm text-gray-600">
            Comprehensive employee turnover insights and predictions
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            <select
              value={selectedPeriod}
              onChange={(e) => {
                const newPeriod = e.target.value;
                setSelectedPeriod(newPeriod);
                if (newPeriod !== "custom") {
                  setDateRange({ startDate: "", endDate: "" });
                }
              }}
              className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 focus:border-blue-500 cursor-pointer"
            >
              <option value="custom">Custom Range</option>
              <option value="7-days">Last 7 Days</option>
              <option value="current-month">Current Month</option>
              <option value="last-month">Last Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="all-data">All Data</option>
            </select>
          </div>

          {selectedPeriod === "custom" && (
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(range) => setDateRange(range)}
              />
            </div>
          )}

          {/* CSV Export */}
          <Button
            variant="outline"
            onClick={() => exportToCSV(attritionData)}
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          icon={Users}
          title="Total Exits"
          value={formatIndianNumber(attritionData.totalExits)}
          color="bg-red-600"
        />

        <KpiCard
          icon={TrendingDown}
          title="Voluntary Exits"
          value={formatIndianNumber(attritionData.voluntaryExits)}
          color="bg-orange-600"
        />

        <KpiCard
          icon={AlertCircle}
          title="Involuntary Exits"
          value={formatIndianNumber(attritionData.involuntaryExits)}
          color="bg-purple-600"
        />
      </div>

      {/* Monthly Attrition Trend & Department-wise Attrition Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Attrition Trend */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Monthly Attrition Trend
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <BarChart3 className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-6">
            {attritionData.monthlyTrends.length > 0 ? (
              attritionData.monthlyTrends.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-gray-600">
                    {item.month}
                  </span>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">
                        Voluntary: {formatIndianNumber(item.voluntary)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">
                        Involuntary: {formatIndianNumber(item.involuntary)}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      Total: {formatIndianNumber(item.total)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">
                  No monthly trend data available
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Department Attrition */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Department-wise Attrition
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Briefcase className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {attritionData.departmentAttrition.map((dept, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="min-w-0 flex-1 pr-4">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {dept.department}
                  </p>
                  <p className="text-xs text-gray-600">
                    {formatIndianNumber(dept.totalEmployees)} employees
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {dept.attritionRate}%
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatIndianNumber(dept.totalExits || dept.exits || 0)} exits
                    </p>
                  </div>
                  {getTrendIcon(dept.trend)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Exits Summary Row */}
      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Exits Summary
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Clock className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-3">
            {attritionData.recentExits && attritionData.recentExits.length > 0 ? (
              attritionData.recentExits.map((exit, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-4 gap-4 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="md:col-span-1 min-w-0">
                    <p className="text-xs font-medium text-gray-500 mb-1">Name</p>
                    <p className="text-sm font-semibold text-gray-900 truncate" title={exit.employeeName}>
                      {exit.employeeName}
                    </p>
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <p className="text-xs font-medium text-gray-500 mb-1">Email</p>
                    <p className="text-xs text-gray-600 truncate" title={exit.employeeEmail}>{exit.employeeEmail}</p>
                  </div>
                  <div className="md:col-span-1 min-w-0">
                    <p className="text-xs font-medium text-gray-500 mb-1">Department</p>
                    <p className="text-xs text-gray-500 truncate" title={exit.department || "N/A"}>
                      {exit.department || "N/A"}
                    </p>
                  </div>
                  <div className="md:col-span-1">
                    <p className="text-xs font-medium text-gray-500 mb-1">Exit Details</p>
                    <p className="text-xs font-medium text-gray-700 mb-1">
                      {exit.lastWorkingDate
                        ? new Date(exit.lastWorkingDate).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                        : "N/A"}
                    </p>
                    <span
                      className={`inline-block px-2 py-0.5 text-xs rounded-full ${exit.resignationType === "involuntary"
                        ? "bg-orange-50 text-orange-600"
                        : "bg-blue-50 text-[#0F2942]"
                        }`}
                    >
                      {exit.resignationType === "involuntary"
                        ? "Involuntary"
                        : exit.resignationType === "retirement"
                          ? "Retirement"
                          : exit.resignationType === "contract_end"
                            ? "Contract End"
                            : "Voluntary"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">No recent exits found</p>
                <p className="text-xs text-gray-400 mt-1">
                  Exits will appear here when employees leave
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttritionAnalysis;
